import { createErrorNotification, createInfoNotification } from '@ulibs/unnotificationsqueue';
import { mapActions, mapGetters } from 'vuex';
import { ActiveWaiter } from '@core/helpers';
import VueRouter from 'vue-router';
import { sentryLogException } from '@core/helpers/sentryLogger';
import { PredictionState } from '@dynameat/views/pointOfSale/models';

export const productMixins = {
  computed: {
    ...mapGetters(['editableProductList']),
    ...mapGetters(['editedProductList']),
    subordinateProductsList() {
      return new Set(this.editableProductList.flatMap((product) => Object.values(product.sale_formats)));
    },
    availableProductsList() {
      return this.editableProductList.filter((product) => !this.subordinateProductsList.has(product.guid));
    },
    availableEditedProductsList() {
      return this.editedProductList.filter((product) => !this.subordinateProductsList.has(product.guid));
    }
  }
};

export const periodMixins = {
  computed: {
    ...mapGetters(['predictionPeriodList']),
    selectedPeriod() {
      return this.predictionPeriodList.find((period) => period.guid === this.$route.params.periodGuid);
    }
  }
};

export const serviceMixins = {
  computed: {
    ...mapGetters(['serviceList']),
    emptyServiceList() {
      return this.serviceList.length === 0;
    }
  }
};

const { isNavigationFailure, NavigationFailureType } = VueRouter;

export const checkPeriodMixin = (periodField, preconditions = []) => ({
  data() {
    return {
      waiter: null,
      recommendationModalVisible: false,
      generationState: null,
    };
  },
  computed: {
    ...mapGetters(['isLoading']),
  },
  methods: {
    ...mapActions(['predictionPeriodStateRequest']),
    async leaveIfNecessary(status) {
      if (status === PredictionState.ERROR) {
        this.addNotification(createErrorNotification(this.$t('errorRecommendations')));
        return false;
      }
      if (status === PredictionState.GENERATING) {
        this.addNotification(createInfoNotification(this.$t('generatingDemands')));
      }
      return true;
    },

    async runFun(periodGuid, stateType) {
      return this.predictionPeriodStateRequest({ periodGuid, stateType });
    },
    async checkPreconditions() {
      await this.setLoading(1);
      let res = true;
      const states = [];
      for (let i = 0; i < preconditions.length; i += 1) {
        states.push(this.predictionPeriodStateRequest({
          periodGuid: this.$route.params.periodGuid,
          stateType: preconditions[i]
        }));
      }
      res = await Promise.all(states);
      const noneGenerating = res.every((el) => el.state !== PredictionState.GENERATING);
      await this.setLoading(-1);
      return noneGenerating;
    },
    async checkPeriod(statePromise) {
      const { state } = await statePromise;
      return state === PredictionState.GENERATED
        || state === PredictionState.ERROR
        || state === PredictionState.PARTIALLY_GENERATED
        || state === PredictionState.INITIAL;
    },
    async reloadPeriod() {
      let timeoutId;
      try {
        await this.setLoading(1);
        timeoutId = setTimeout(() => {
          if (!this.recommendationModalVisible) this.recommendationModalVisible = true;
        }, 500);
        this.waiter = new ActiveWaiter(this.runFun, this.checkPeriod, 1000, [this.$route.params.periodGuid, periodField]);
        const res = await this.waiter.initialize();
        clearTimeout(timeoutId);
        this.recommendationModalVisible = false;
        await this.setLoading(-1);
        const shouldContinue = await this.leaveIfNecessary(res.state);
        this.generationState = res.state;
        return shouldContinue;
      } catch (err) {
        sentryLogException(err, 'Error reloding period');
        clearTimeout(timeoutId);
        this.recommendationModalVisible = false;
        if (this.isLoading) {
          await this.setLoading(-1);
        }

        this.addNotification(createErrorNotification(this.$t('periodLoadingError'), null, 5000));
        this.$router.push({
          name: 'optimizer',
          params: { periodGuid: this.$route.params.periodGuid }
        }).catch((e) => {
          if (!isNavigationFailure(e, NavigationFailureType.aborted)) {
            Promise.reject(e);
          }
        });
      }
      return false;
    },
  }
});
